import React from 'react';

const PLAN_ACCENTS = ['#22c55e', '#3b82f6', '#f59e0b', '#ec4899', '#14b8a6'];

function formatPlanPrice(value) {
  const amount = Number(value);
  if (Number.isNaN(amount)) return value || '$0';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: amount % 1 === 0 ? 0 : 2 }).format(amount);
}

function formatBillingPeriod(value) {
  if (!value) return '/month';
  return value.startsWith('/') ? value : `/${value}`;
}

function formatFeatureLabel(code) {
  if (!code) return 'Feature';
  return code.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function summarizePlan(features = []) {
  const parts = features.slice(0, 2).map((feature) => `${formatFeatureLabel(feature.code)}: ${feature.value}`);
  if (parts.length === 0) return 'Plan details loaded from the subscription database.';
  return parts.join(' • ');
}

export default function PlansPage({ planData, plans, loading, err, STYLES }) {
  const activePlanName = planData?.plan ? String(planData.plan).toLowerCase() : '';

  return (
    <div style={STYLES.plansWrap}>
      <div style={STYLES.plansHero}>
        <section style={STYLES.plansHeroCard}>
          <div style={STYLES.plansHeroKicker}>Pricing</div>
          <div style={STYLES.plansHeroTitle}>Choose a testing plan that matches your email traffic.</div>
          <div style={STYLES.plansHeroText}>
            Compare plans, monthly pricing, and included features in one place. This page stays inside the current app shell so it works without adding routing dependencies.
          </div>
        </section>

        <aside style={STYLES.plansCurrentCard}>
          <div style={STYLES.plansCurrentLabel}>Current subscription</div>
          <div style={STYLES.plansCurrentName}>{planData?.plan || 'No active plan'}</div>
          <div style={STYLES.plansCurrentText}>
            {planData?.plan
              ? 'Your active plan is highlighted below so you can compare it against the other tiers.'
              : 'No active subscription was found for this user. You can still review the available plans below.'}
          </div>
        </aside>
      </div>

      {err ? (
        <div style={STYLES.errMsg}>{err}</div>
      ) : loading ? (
        <div style={{ ...STYLES.emptyText, marginTop: 40 }}>Loading plans...</div>
      ) : plans.length === 0 ? (
        <div style={{ ...STYLES.emptyText, marginTop: 40 }}>No plans found in the database.</div>
      ) : (
        <div style={STYLES.plansGrid}>
          {plans.map((plan, index) => {
            const active = activePlanName === plan.name.toLowerCase();
            const accent = PLAN_ACCENTS[index % PLAN_ACCENTS.length];

            return (
              <section key={plan.id} style={STYLES.planCard(accent, active)}>
                <div style={STYLES.planBadgeRow}>
                  <div style={STYLES.planName}>{plan.name}</div>
                  <div style={STYLES.planBadge(accent, active)}>{active ? 'Current' : 'Available'}</div>
                </div>

                <div style={STYLES.planPriceRow}>
                  <span style={STYLES.planPrice}>{formatPlanPrice(plan.price)}</span>
                  <span style={STYLES.planBilling}>{formatBillingPeriod(plan.billing)}</span>
                </div>

                <div style={STYLES.planSummary}>{summarizePlan(plan.features)}</div>

                <div style={STYLES.planFeatureList}>
                  {plan.features.map((feature) => (
                    <div key={`${plan.id}-${feature.code}`} style={STYLES.planFeatureItem}>
                      <span style={STYLES.planFeatureDot(accent)}>✓</span>
                      <span>{formatFeatureLabel(feature.code)}: {feature.value}</span>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}